import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { RootState } from "redux/store";
import * as env from "shared/env";
import { Dispatch, bindActionCreators } from "redux";
import { styles } from "./style";
import { connect } from "react-redux";
import { Dialog } from "component/shared/dialog";
import { getAgentsWithLeads } from "redux/selector";
import { getPostalCodes } from "redux/selector";
import { Select } from "component/shared/select";
import { GeographicRoute, NewGeographicRoute } from "model/geographic-route";
import { saveGeographicRoute } from "redux/slice/geographic-routes";
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import { Agent } from "model/agent";

const mapStateToProps = (state: RootState) => {
	return {
		loading: state.leads.loading,
		agents: getAgentsWithLeads(state),
		postalCodes: getPostalCodes(state),
	};
};

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	saveGeographicRoute
}, dispatch);

interface OwnProps {
	agentRoutes: {
		agent: Agent;
		routes: GeographicRoute[];
	}[];
	open: boolean;
	onClose: () => void;
	onSave: (geographicRoute: NewGeographicRoute) => void;
}

interface Props extends
	OwnProps,
	ReturnType<typeof mapStateToProps>,
	ReturnType<typeof mapDispatchToProps>,
	WithStyles<typeof styles>
{}

interface State {
	submitted: boolean;
	selectedPostalCodes: string[];
	agentId: number;
}

const initialState: State = {
	submitted: false,
	selectedPostalCodes: [],
	agentId: 0
}

class Component extends React.Component<Props, State> {
	public constructor(props: Props) {
		super(props);
		this.state = initialState;
	}

	private inputRef: React.RefObject<HTMLInputElement> = React.createRef();

	private agentRoutExists(agent: Agent) {
		const { agentRoutes } = this.props;
		return agentRoutes.some((agentRoute) => agentRoute.agent.id === agent.id)
	}

	private resetState() {
		this.setState({selectedPostalCodes: [], agentId: 0 });
	}

	public render() {
		const { agentId, selectedPostalCodes } = this.state;
		const { open, agentRoutes, agents, postalCodes, onSave, onClose} = this.props;
		return (
			<Dialog open={open} onClose={() => onClose()} scroll="paper">
				<Mui.DialogTitle>
					Add Geographic Lead Routing Rule
				</Mui.DialogTitle>
				<Mui.DialogContent>
					<Mui.Grid container spacing={5} direction="column">
						<Mui.Grid item>
							<Select
								fullWidth
								variant="outlined"
								margin="dense"
								placeholder="Select an agent"
								value={agentId}
								options={agents}
								valueExtractor={agent => agent.id}
								labelExtractor={agent => agent.name}
								optionRenderer={agent => <Mui.ListItemText primary={agent.name} />}
								onChange={(value: number) => this.setState({agentId: value})}
							/>
						</Mui.Grid>
						<Mui.Grid item>
							<Autocomplete
								value={[...selectedPostalCodes]}
								multiple
								freeSolo
								id="postal-code"
								options={postalCodes.map((option) => option?.postalCode)}
								getOptionDisabled={(option) => {
									const disabledPostalCodes = agentRoutes.flatMap(agentRoute => agentRoute.routes.map(route => route.postalCode));
									return option ? disabledPostalCodes.includes(option) : false;
								}}
								renderTags={(value, getTagProps) =>
								value.map((option, index) => (
									<Chip
										variant="outlined"
										label={option}
										{...getTagProps({ index })}
									/>
								))
								}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Postal Codes"
										placeholder="Add..."
										inputRef={this.inputRef}
									/>
								)}
								onPaste={(event) => {
									event.preventDefault();
									const pastedText = event.clipboardData.getData("text/plain");
									const codes = pastedText.split(/[,\s\n]+/).filter(Boolean);
									const uniques = Array.from(new Set([...selectedPostalCodes, ...codes]));
									this.setState({ selectedPostalCodes: [...uniques] });
								}}
								onChange={(event, values) => {
									this.setState({ 
										selectedPostalCodes: values as string[],
									})
								}}
							/>
						</Mui.Grid>
					</Mui.Grid>
				</Mui.DialogContent>
				<Mui.DialogActions>
					<Mui.Button
						variant="outlined"
						color={"primary"}
						onClick={() => {
							this.resetState();
							onClose();
						}}
					>
						Cancel
					</Mui.Button>
					<Mui.Button
						variant="contained"
						color={"primary"}
						onClick={() => {
							const singleValue = this.inputRef.current?.value.trim();
							const isSingleValue = singleValue && selectedPostalCodes.length < 1;
							if (isSingleValue) {
								selectedPostalCodes.push(singleValue);
							}
							selectedPostalCodes.forEach((postalCode) => {
								const agent = this.props.agents.find((agent) => agent.id === agentId)
								if (agent) {
									const geographicRoute: NewGeographicRoute = {
										agent: agent,
										postalCode: postalCode,
									}
									onSave(geographicRoute);
									this.resetState();
									onClose();
								}
							})
						}}
					>
						Add
					</Mui.Button>
				</Mui.DialogActions>
			</Dialog>
		);
	}
}
export const AddDialog = withStyles(connect(
	mapStateToProps,
	mapDispatchToProps,
)(Component), styles);
